/*
 * Accessibility runtime fixes (Brooks Creative Studio, Oct 2026)
 *
 * The theme's dialog components (drawers, modals, popovers, dropdown menus) add role="dialog"
 * in JavaScript but not always aria-modal or an accessible name. This fills those in, using the
 * dialog's heading or the text of the button/link that opens it. It also makes horizontally
 * scrollable regions with no focusable content reachable by keyboard.
 *
 * Self-contained: removing this file (and its <script> tag in layout/theme.liquid) reverts it.
 */
const clean = (text) => (text || '').replace(/\s+/g, ' ').trim().slice(0, 80);

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

function dialogName(dialog) {
  const title = dialog.getAttribute('title');
  if (clean(title)) return clean(title);

  const heading = dialog.querySelector('[slot="header"] :is(h1, h2, h3, h4, p), [slot="header"], :scope > :is(h1, h2, h3, h4), .drawer__title, .popover__title');
  if (heading && clean(heading.textContent)) return clean(heading.textContent);

  if (dialog.id) {
    const control = document.querySelector(`[aria-controls="${CSS.escape(dialog.id)}"]`);
    if (control) {
      const label = control.getAttribute('aria-label') || control.textContent;
      if (clean(label)) return clean(label);
    }
  }

  return 'Dialog';
}

function fixDialog(dialog) {
  if (!dialog.hasAttribute('aria-modal')) {
    dialog.setAttribute('aria-modal', 'true');
  }
  if (!dialog.hasAttribute('aria-label') && !dialog.hasAttribute('aria-labelledby')) {
    dialog.setAttribute('aria-label', dialogName(dialog));
  }
}

function fixScrollRegion(region) {
  if (region.hasAttribute('tabindex')) return;
  if (region.scrollWidth <= region.clientWidth && region.scrollHeight <= region.clientHeight) return;
  if (region.querySelector(FOCUSABLE)) return;
  region.setAttribute('tabindex', '0');
  if (!region.hasAttribute('role')) region.setAttribute('role', 'region');
  if (!region.hasAttribute('aria-label') && !region.hasAttribute('aria-labelledby')) {
    region.setAttribute('aria-label', 'Scrollable content');
  }
}

function run(root = document) {
  root.querySelectorAll('[role="dialog"], [role="alertdialog"]').forEach(fixDialog);
  root.querySelectorAll('scroll-carousel, .scroll-area').forEach(fixScrollRegion);
}

run();

// Dialogs and carousels are also added later (quick buy, AJAX product lists, search results).
let queued = false;
new MutationObserver(() => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    run();
  });
}).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['role'] });

window.addEventListener('resize', () => document.querySelectorAll('scroll-carousel, .scroll-area').forEach(fixScrollRegion), { passive: true });
